import { Module } from 'vuex';
import JobEvenMessage from '@/dossier/constants/JobEvenMessage';
import DmsClient from '@/general/client/DmsClient';
import pushService from '@/general/services/PushService';
import { RootState, VehicleState } from '@/general/store/states';
import DmsSyncType from '@/shared/constants/DmsSyncType';
import VehicleSearchType from '@/shared/constants/VehicleSearchType';
import DmsSyncStatus from '@/shared/models/DmsSyncStatus';
import { OutletId } from '@/shared/models/Outlet';
import { DmsName } from '@/shared/models/common';
import VehicleClient from '../client/VehicleClient';
import VehicleSearchStatus from '../constants/VehicleSearchStatus';
import Vehicle from '../models/Vehicle';

export class DmsVehicleNotFound extends Error {
}

export class DmsNotProcessed extends Error {
}

export interface SearchPayload {
  keyword: string;
  type: VehicleSearchType;
  dmsConnectionName?: DmsName;
  outletId: OutletId;
  dms: boolean;
}

const module: Module<VehicleState, RootState> = {
  namespaced: true,

  state: {
    current: null,
    searchStatus: VehicleSearchStatus.IDLE,
  },

  mutations: {
    setCurrent(state, vehicle: Vehicle | null = null): void {
      state.current = vehicle;
    },

    searchStatus(state, searchStatus: VehicleSearchStatus): void {
      state.searchStatus = searchStatus;
    },

    clear(state): void {
      state.current = null;
      state.searchStatus = VehicleSearchStatus.IDLE;
    },
  },

  actions: {
    async search({ state, commit }, { keyword, type, outletId, dms = false, dmsConnectionName }: SearchPayload): Promise<Array<Vehicle>> {
      if (!keyword) throw new Error('keyword is required for search');
      if (state.searchStatus !== VehicleSearchStatus.IDLE) throw new Error('search already active');

      try {
        commit('searchStatus', VehicleSearchStatus.ACTIVE);

        let vehicles = await VehicleClient.search(keyword, type, outletId, dmsConnectionName);

        if (vehicles.length === 0 && dms) {
          const dmsObject = await DmsClient.getDmsObjects(type, keyword, outletId, dmsConnectionName);

          switch (dmsObject.dmsSyncType) {
            case DmsSyncType.INSTANT:
              switch (dmsObject.payload) {
                case 'not_found':
                  throw new DmsVehicleNotFound();
                case 'imported_but_not_processed':
                  throw new DmsNotProcessed();
                case 'processed':
                  break;
                default:
                  throw new Error(`unexpected payload: ${dmsObject.payload}`);
              }
              break;
            case DmsSyncType.LAZY:
              if (!(dmsObject.payload instanceof DmsSyncStatus)) throw new Error('unexpected DMS payload');
              await pushService.sequence(`${dmsObject.payload.pusherChannel}/${dmsObject.payload.pusherChannelEventName}`, (event, resolve, reject) => {
                switch (event.eventMessage) {
                  case JobEvenMessage.IMPORTED:
                    commit('searchStatus', VehicleSearchStatus.DMS_PROCESSING);
                    break;
                  case JobEvenMessage.PROCESSED:
                    resolve();
                    break;
                  case JobEvenMessage.NO_DATA:
                    reject(new DmsVehicleNotFound());
                    break;
                }
              });
              break;
            default:
              throw new DmsVehicleNotFound();
          }

          vehicles = await VehicleClient.search(keyword, type, outletId, dmsConnectionName);
        }
        return vehicles;
      } finally {
        commit('searchStatus', VehicleSearchStatus.IDLE);
      }
    },
  },
};

export default module;